import {HttpErrorResponse} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {Router} from '@angular/router';
import {LocalStorageService} from '@app/core/services/local-storage.service';
import {ToastMessageUtils} from '@app/core/utils/toast-message.utils';
import {EToastMessageOptions, EToastMessageSeverity} from '@app/shared/enums/toast-message.enum';
import {IMedicalSpecialty} from '@app/shared/models/medical-specialty.model';
import {environment} from '@environments/environment';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {MessageService} from 'primeng/api';
import {catchError, delay, from, map, of, switchMap, tap} from 'rxjs';
import {HttpErrorConverter} from 'src/app/core/utils/httpErrorConverter.utils';
import {ICity} from 'src/app/shared/models/city.model';
import {ICountry} from 'src/app/shared/models/country.model';
import {IRegion} from 'src/app/shared/models/region.model';
import {IReCaptcha} from '../models/grecaptcha.model';
import {SignUpService} from '../services/sign-up.service';
import {
  SignUpActions,
  getCaptchaResponseFailure,
  getCaptchaResponseSuccess,
  getRecaptchaTokenFailure,
  getRecaptchaTokenSuccess,
  loadCities,
  loadCitiesFromInstitutionsSettings,
  loadCitiesSuccess,
  loadCountriesSuccess,
  loadMedicalSpecialtiesSuccess,
  loadRegions,
  loadRegionsFromInstitutionsSettings,
  loadRegionsSuccess,
  resendLink,
  resendLinkFailure,
  resendLinkSuccess,
  resetCaptchaFromSignupEffects,
  signUpFailure,
  signUpRequest,
  signUpSuccess,
} from './sign-up.actions';

declare const grecaptcha: IReCaptcha;

@Injectable()
export class SignUpEffects {
  private readonly actions$ = inject(Actions);
  private readonly signUpService = inject(SignUpService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly oidcSecurityService = inject(OidcSecurityService);

  private readonly waitToSeeThankYouPageTwoSeconds = 2 * 1000;
  globalToast = 'global-toast';

  loadCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpActions.LoadCountries, SignUpActions.LoadCitiesFromInstitutionsSettings),
      switchMap(() => this.loadCountries())
    )
  );

  loadMedicalSpecialties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpActions.LoadMedicalSpecialties),
      switchMap(() => this.loadMedicalSpecialties())
    )
  );

  loadRegions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpActions.LoadRegions, SignUpActions.LoadRegionsFromInstitutionsSettings),
      switchMap((action: ReturnType<typeof loadRegions | typeof loadRegionsFromInstitutionsSettings>) =>
        this.loadRegions(action)
      )
    )
  );

  loadCities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpActions.LoadCities, SignUpActions.LoadCitiesFromInstitutionsSettings),
      switchMap((action: ReturnType<typeof loadCities>) => this.loadCities(action))
    )
  );

  signUpRequest1$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpActions.SignUpRequest),
      switchMap((action: ReturnType<typeof signUpRequest>) => {
        return this.signUpRequest(action);
      })
    )
  );

  resendLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpActions.ResendLink),
      switchMap((action: ReturnType<typeof resendLink>) => this.resendLink(action))
    )
  );

  getRecaptchaToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpActions.GetReCaptchaToken),
      switchMap(() => this.getRecaptchaToken())
    )
  );

  getCaptchaResponse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpActions.GetReCaptchaTokenSuccess),
      switchMap((action: ReturnType<typeof getRecaptchaTokenSuccess>) => this.getCaptchaResponse(action))
    )
  );

  private loadCountries() {
    return this.signUpService.getCountries()?.pipe(
      map((countries: ICountry[]) => {
        return loadCountriesSuccess({countries: countries});
      })
    );
  }

  private loadMedicalSpecialties() {
    return this.signUpService.getMedicalSpecialties()?.pipe(
      map((medicalSpecialties: IMedicalSpecialty[]) => {
        return loadMedicalSpecialtiesSuccess({
          medicalSpecialties: medicalSpecialties,
        });
      })
    );
  }

  private loadRegions(action: ReturnType<typeof loadRegions | typeof loadRegionsFromInstitutionsSettings>) {
    return this.signUpService.getRegions(action.countryId)?.pipe(
      map((regions: IRegion[]) => {
        return loadRegionsSuccess({regions: regions});
      })
    );
  }
  private loadCities(action: ReturnType<typeof loadCities | typeof loadCitiesFromInstitutionsSettings>) {
    return this.signUpService.getCities(action.countryId, action.regionId)?.pipe(
      map((cities: ICity[]) => {
        return loadCitiesSuccess({cities: cities});
      })
    );
  }

  private signUpRequest(action: ReturnType<typeof signUpRequest>) {
    return this.signUpService.signUpRequest(action.request).pipe(
      map(() => {
        this.showSuccessMessage(
          this.translate.instant('SignUp.SuccessfullyCreatedAccount'),
          this.translate.instant('SignUp.AccountRequested')
        );
        this.router.navigateByUrl('/signup/thank-you');
        this.store.dispatch(resetCaptchaFromSignupEffects());
        this.localStorageService.clear();
        return signUpSuccess();
      }),
      delay(this.waitToSeeThankYouPageTwoSeconds),
      tap(() => this.oidcSecurityService.authorize()),
      catchError((errorResult: HttpErrorResponse) => {
        this.showErrorMessage(
          this.translate.instant('SignUp.CreatedAccountFailed'),
          this.translate.instant('SignUp.AccountRequestedFailed')
        );
        return of(
          signUpFailure({
            reason: HttpErrorConverter.convertToString(errorResult),
          })
        );
      })
    );
  }

  private resendLink(action: ReturnType<typeof resendLink>) {
    return this.signUpService.resendLink(action.email)?.pipe(
      map((linkSent: string) => {
        if (linkSent) {
          this.showSuccessMessage(this.translate.instant('ExpiredToken.LinkSentSuccessfully'));
          return resendLinkSuccess();
        }
        this.resendLinkFailureMessage();
        return resendLinkFailure();
      }),
      catchError((errorResult: HttpErrorResponse) => {
        console.log(errorResult);
        this.resendLinkFailureMessage();
        return of(resendLinkFailure());
      })
    );
  }

  private resendLinkFailureMessage(): void {
    this.showErrorMessage(this.translate.instant('ExpiredToken.LinkSentFailure'));
  }

  private getRecaptchaToken() {
    const response = grecaptcha.execute(environment.recaptcha.siteKey, {
      action: 'importantAction',
    });
    return from(response).pipe(
      map((token: string) => {
        return getRecaptchaTokenSuccess({recaptchaToken: token});
      }),
      catchError((errorResult: HttpErrorResponse) => {
        const errMsg = this.getErrorMessage(errorResult.status);
        this.showErrorMessage(errMsg);
        return of(getRecaptchaTokenFailure());
      })
    );
  }

  private getCaptchaResponse(action: ReturnType<typeof getRecaptchaTokenSuccess>) {
    return this.signUpService.getRecaptchaResponse(action.recaptchaToken)?.pipe(
      map((captchaResponse: boolean) => {
        if (captchaResponse) {
          return getCaptchaResponseSuccess({
            captchaResponse: captchaResponse,
          });
        }
        this.captchaErrorMessage();
        return getCaptchaResponseFailure();
      }),
      catchError((errorResult: HttpErrorResponse) => {
        const errMsg = this.getErrorMessage(errorResult.status);
        this.showErrorMessage(errMsg);
        return of(getCaptchaResponseFailure());
      })
    );
  }

  private captchaErrorMessage(): void {
    this.showErrorMessage(this.translate.instant('SignUp.CaptchaError'));
  }

  private getErrorMessage(statusCode: number): string {
    let errorMessage = '';
    switch (statusCode) {
      case 400:
        errorMessage = this.translate.instant('Common.400_Error');
        break;
      case 401:
        errorMessage = this.translate.instant('Common.401_Error');
        break;
      case 404:
        errorMessage = this.translate.instant('Common.404_Error');
        break;
      case 403:
        errorMessage = this.translate.instant('Common.403_Error');
        break;
      case 500:
        errorMessage = this.translate.instant('Common.500_Error');
        break;
      default:
        errorMessage = this.translate.instant('SignUp.CaptchaError');
        break;
    }
    return errorMessage;
  }

  private showErrorMessage(errMsg: string, title?: string): void {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Error,
      messageDetail: errMsg,
      closable: true,
      life: EToastMessageOptions.LifeTime,
      summary: title,
    });
  }

  private showSuccessMessage(msg: string, title?: string): void {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Success,
      messageDetail: msg,
      closable: true,
      life: EToastMessageOptions.LifeTime,
      summary: title,
    });
  }
}
